import {
  Anchor,
  Box,
  Button,
  Container,
  Divider,
  Group,
  Paper,
  PasswordInput,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate } from 'react-router-dom';
import { X } from 'tabler-icons-react';
import axios from '../../api/axios';
import { MerchantOnboardingStatus } from '../../enums/merchant-onboarding-status';
import { Roles } from '../../enums/roles-enum';
import useAuth from '../../hooks/useAuth';
import { ErrorResponse } from '../../type/error-response.types';
import { SignInRequest, SignInResponse } from '../../type/sign-in.types';
import { FacebookButton } from '../social-buttons/facebook-button.component';
import './sign-in-form.styles.css';

function SignInForm() {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const LOGIN_URL = '/web/auth/signin';

  const handleLoginSubmit = async () => {
    setLoading(true);

    if (!executeRecaptcha) {
      return;
    }

    const recaptchaToken = await executeRecaptcha('signin');

    const signInRequest: SignInRequest = {
      username: form.values.username,
      password: form.values.password,
      recaptchaToken: recaptchaToken,
    };

    console.log("signInRequest: ", signInRequest);

    axios
      .post(LOGIN_URL, signInRequest, { withCredentials: true })
      .then((res) => {
        console.log(res.data);
        const resData: SignInResponse = res.data;
        const {
          id,
          type,
          user,
          accessToken,
          role,
          onboardingStatus,
          onboardingReferenceNumber,
        } = resData;
        const { email } = user;

        setAuth({
          id,
          type,
          username: user.username,
          accessToken,
          email,
          role,
          onboardingStatus,
          onboardingReferenceNumber: onboardingReferenceNumber,
        });

        if (
          role === Roles.ADMINISTRATOR ||
          role === Roles.SETTLEMENT ||
          role === Roles.CHECKER
        ) {
          navigate('/admin');
        } else if (onboardingStatus === MerchantOnboardingStatus.APPROVED) {
          navigate('/dashboard');
        } else {
          navigate('/activate');
        }
      })
      .catch((err) => {
        
        console.error(err);

        const error: ErrorResponse = err.response?.data;

        let message = "";
        if (error != null) {
          message = error.error || 'An Unexpected Error Occurred';
        }
        else {
          message = err;
        }

        showNotification({
          id: 'failed-to-login',
          message,
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
        setLoading(false);
      });
  };

  const validate = (value: string) => {
    if (value.length === 0) {
      return 'This field is required.';
    }
  };

  const form = useForm({
    initialValues: {
      username: '',
      password: '',
    },

    //TODO Stricter validation
    validate: {
      username: (value: string) => validate(value),
      password: (value: string) => validate(value),
    },
  });

  return (
    <>
      <Container fluid>
        <Paper
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          py={30}
          px={40}
          withBorder
          shadow='lg'
          radius='md'
        >
          <Box sx={{ width: '100%', maxWidth: 450 }}>
            <Text size='lg' weight={500} align='center'>
              Sign in to your account
            </Text>
            <Group mb='md' mt='md' grow>
              <FacebookButton radius='sm' />
            </Group>

            <Divider
              label='or continue with email'
              labelPosition='center'
              my='lg'
            />

            <form onSubmit={form.onSubmit(handleLoginSubmit)} noValidate>
              <Group direction='column' grow>
                <TextInput
                  required
                  label='Username'
                  placeholder='Username'
                  value={form.values.username}
                  onChange={(event) =>
                    form.setFieldValue('username', event.currentTarget.value)
                  }
                  error={form.errors.username}
                  data-cy='username'
                />

                <PasswordInput
                  required
                  name='password'
                  label='Password'
                  placeholder='Password'
                  value={form.values.password}
                  onChange={(event) =>
                    form.setFieldValue('password', event.currentTarget.value)
                  }
                  error={form.errors.password}
                  data-cy='password'
                />
              </Group>

              <Anchor
                component='button'
                type='button'
                color='gray'
                onClick={() => {
                  navigate('/forgot-password');
                }}
                size='xs'
              >
                Forgot your password?
              </Anchor>

              <Group position='apart' mt='xl'>
                <Button
                  type='button'
                  color='green'
                  onClick={() => {
                    navigate('/register');
                  }}
                >
                  Sign up
                </Button>
                <Button type='submit' loading={loading} data-cy='submit'>
                  {loading ? 'Loading' : 'Login'}
                </Button>
              </Group>
            </form>
          </Box>
        </Paper>
      </Container>
    </>
  );
}

export default SignInForm;
